import React from "react";
import PropTypes from "prop-types";

/**
 * 狀態標籤（design system primitive）。
 * 樣式對齊模板 `/ui-general` 與 Ecommerce orders 的 pill：
 * `badge rounded-pill badge-soft-* font-size-12`（淡底深字）或 `bg-*`（實底白字）。
 *
 *   <StatusTag status="approved" />          // 成功
 *   <StatusTag variant="default">審核</StatusTag>
 *   <StatusTag variant="gary">未發佈</StatusTag>
 *   <StatusTag color="info" soft>自訂</StatusTag>
 */

// 變體 → 模板 badge modifier（soft = 淡底深字；實底用 bg-*）
const VARIANT_CLASS = {
  error: "badge-soft-danger",
  success: "badge-soft-success",
  warning: "badge-soft-warning",
  default: "bg-primary",
  plain: "bg-dark",
  gary: "badge-soft-secondary",
  redsolid: "badge-soft-danger",
  info: "badge-soft-info",
};

// 語意狀態 → 變體 + 預設文字
const STATUS_MAP = {
  // 審核
  approved: { variant: "success", text: "已通過" },
  pending: { variant: "warning", text: "審核中" },
  rejected: { variant: "error", text: "已退回" },
  draft: { variant: "gary", text: "草稿" },
  // 一般啟用/停用
  active: { variant: "success", text: "啟用" },
  inactive: { variant: "gary", text: "停用" },
  paused: { variant: "warning", text: "暫停" },
  // 資產/設備
  running: { variant: "success", text: "運行中" },
  idle: { variant: "gary", text: "閒置" },
  repair: { variant: "warning", text: "維修中" },
  offline: { variant: "error", text: "離線" },
  closed: { variant: "gary", text: "已結案" },
  // 硬體上架階段(兩段式新增,ticket 01)。與上面的設備狀態是兩件事:
  // 那些講設備健康度,這兩個講「機器到底進機房了沒」。
  reserved: { variant: "info", text: "已核准待上架" },
  commissioned: { variant: "success", text: "已上架" },
  // 風險等級
  critical: { variant: "error", text: "嚴重" },
  high: { variant: "error", text: "高" },
  medium: { variant: "warning", text: "中" },
  low: { variant: "gary", text: "低" },
};

function badgeModifier({ status, variant, color, soft }) {
  if (color) {
    return soft ? `badge-soft-${color}` : `bg-${color}`;
  }
  const meta = status ? STATUS_MAP[status] : null;
  const key = variant || (meta && meta.variant) || "gary";
  return VARIANT_CLASS[key] || VARIANT_CLASS.gary;
}

export const StatusTag = ({ status, variant, color, soft, children, className }) => {
  const meta = status ? STATUS_MAP[status] : null;
  const label = children || (meta ? meta.text : status);
  const modifier = badgeModifier({ status, variant, color, soft });
  return (
    <span
      className={["badge", "rounded-pill", "font-size-12", modifier, "dck-status-tag", className]
        .filter(Boolean)
        .join(" ")}
    >
      {label}
    </span>
  );
};
StatusTag.propTypes = {
  status: PropTypes.string,
  variant: PropTypes.oneOf(["error", "success", "warning", "default", "plain", "gary", "redsolid", "info"]),
  color: PropTypes.string,
  soft: PropTypes.bool,
  children: PropTypes.node,
  className: PropTypes.string,
};

export default StatusTag;
